import { useCallback, useState } from 'react';
import { SelloutProductColor } from '../../../../../../../../services/modules/product-color-sellout/product-color-sellout.types';
import { sleep } from '../../../../../../../../utils/generics';
import { packDisallowChange } from '../../../../../../../grids/utils/packs.utils';
import { UpdateItemFormat } from '../../../../../orderProducts.types';
import useOrderProductGridContext from '../../../contexts/OrderProductGrid.context';
import { useGridStoreApiOptional } from '../../../store/GridStoreProvider';
import { produceItemsToStore } from '../../../store/produceItemsToStore';
import { SelloutDistribution } from '../components/distribution/types/selloutDistribution.types';
import { getSelloutPiecesDistribution } from '../components/distribution/utils/selloutDistribution.utils';
import { getAvailableSelloutMetrics } from '../utils/useSelloutModal.utils';

interface UseSelloutModalParams {
  onClose: () => void;
  readMode: boolean;
  selloutMetrics: SelloutProductColor;
}

const useSelloutModal = ({ selloutMetrics, onClose, readMode }: UseSelloutModalParams) => {
  const [pieces, setPieces] = useState(0);

  const { stackItem, grid, product, orderId, gridItemSkuIdsErrorAndDisabled } =
    useOrderProductGridContext();
  const gridStoreApi = useGridStoreApiOptional();
  const combinationKey = product.combinationKey;

  const packDisableGrid = packDisallowChange(
    product.availability,
    product.productAllowChangePreOrderGrid,
    product.productAllowChangeImmediateGrid,
  );

  const disabled = readMode || packDisableGrid;

  const availableSelloutMetrics = getAvailableSelloutMetrics(
    gridItemSkuIdsErrorAndDisabled,
    selloutMetrics,
  );

  const distribution: SelloutDistribution[] = getSelloutPiecesDistribution(
    availableSelloutMetrics,
    pieces,
  );

  const handleApplyGrid = async () => {
    const newGrid: UpdateItemFormat[] = grid.map((gridItem) => ({
      availability: product.availability,
      deliveryWindowId: product.deliveryWindowId,
      id: gridItem.orderItemId,
      orderConditionId: gridItem.orderConditionId,
      productColorId: product.productColorId,
      quantity:
        distribution.find(({ skuId }) => skuId === gridItem.skuId)?.quantityToDistribute || 0,
      skuId: gridItem.skuId,
    }));

    if (gridStoreApi && combinationKey) {
      produceItemsToStore({
        api: gridStoreApi,
        combinationKey,
        items: newGrid.map((item) => ({ ...item, orderId: orderId ?? '' })),
      });
    } else {
      newGrid.forEach(stackItem);
    }
    onClose();

    await sleep(100);
    setPieces(0);
  };

  const onChangePieces = useCallback((value: number) => setPieces(value), []);

  return {
    pieces,
    disabled,
    distribution,
    handleApplyGrid,
    onChangePieces,
  };
};

export default useSelloutModal;
